import { API_ROUTES } from "../../../../../shared/app-configs/sc-app-config";
import { TCAppConfig } from "../../../../../shared/app-configs/tc-app-configs";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { ComponentRawMaterialDetails } from "../../../sc-models/facility-product-details-dto";

/**
 * Fetch product details using the given API caller.
 *
 * @param executeGet - The API call function from your custom hook.
 * @param scId - The scId parameter (e.g. from useParams() as SC_ID).
 * @param facilityId - Array of facility identifiers.
 * @param productId - The product identifier.
 * @returns A promise that resolves with the product details.
 * @throws An error if the fetch fails.
 */
export async function fetchProductDetails(
  executeGet: (url: string, params: Record<string, any>) => Promise<any>,
  scId: string,
  facilityId: string,
  productId: string
): Promise<any> {
  if (!scId || !facilityId?.length || !productId) return null;

  try {
    const response = await executeGet(
      API_ROUTES.GET_PRODUCT_FACILITY_DETAILS_NEW,
      {
        scId,
        facilityId,
        productId,
      }
    );
    return response;
  } catch (err) {
    console.error("Error fetching product details:", err);
    throw new Error("Failed to load product details.");
  }
}

interface ProductRow {
  id: string;
  facilityId: string;
  scId: string;
  productDetails: {
    productNo: string;
    standardLabelGrade: string; // We'll join array into a comma-separated string
    productCategory: string;
    productCategoryUserSpecificTerm: string;
    productDetail: string;
    productDetailUserSpecificTerm: string;
  };
}

export function mapProductData(rawProducts: any): any[] {
  try {
    let productArray = rawProducts;

    // If response is wrapped inside { response: [...] }
    if (
      rawProducts &&
      rawProducts.response &&
      Array.isArray(rawProducts.response)
    ) {
      console.warn(
        "⚠️ rawProducts contains a wrapper object, extracting response array."
      );
      productArray = rawProducts.response;
    }

    if (!Array.isArray(productArray)) {
      console.error(
        "❌ Invalid input: rawProducts is not an array.",
        rawProducts
      );
      return [];
    }

    console.log(`✅ Mapping ${productArray.length} product(s)...`);

    const mappedData = productArray.map((product, index) => {
      try {
        console.log(`🔄 Mapping product at index ${index}:`, product);

        const mappedProduct: any = {
          productId: product.productId || "",
          facilityId: product.facilityId,
          scId: product.scId || "",
          productDetails: {
            productNo: product.scProductNo || "",
            standardLabelGrade: (() => {
              const labelGrade = product.scProductStandardLabelGrade;
              // Normalize to array: handle both string and array types
              if (Array.isArray(labelGrade)) {
                return labelGrade.filter((item) => item && item.trim().length > 0).join(", ");
              } else if (typeof labelGrade === "string" && labelGrade.trim().length > 0) {
                return labelGrade.trim();
              }
              return "";
            })(),
            productCategory: product.scProductCategoryCode || "",
            productCategoryUserSpecificTerm: (() => {
              // Fallback: use scProductDetailUserSpecificTerm if scProductCategoryUserSpecificTerm is not present
              const term = product.scProductCategoryUserSpecificTerm || product.scProductDetailUserSpecificTerm;
              // Normalize: handle both string and array types
              if (Array.isArray(term)) {
                return term.filter((item) => item && item.trim().length > 0).join(", ");
              } else if (typeof term === "string" && term.trim().length > 0) {
                return term.trim();
              }
              return "";
            })(),
            productDetail: product.scProductDetailCode || "",
            productDetailUserSpecificTerm: (() => {
              const term = product.scProductDetailUserSpecificTerm;
              // Normalize: handle both string and array types
              if (Array.isArray(term)) {
                return term.filter((item) => item && item.trim().length > 0).join(", ");
              } else if (typeof term === "string" && term.trim().length > 0) {
                return term.trim();
              }
              return "";
            })(),
          },
        };

        console.log(
          `✅ Successfully mapped product at index ${index}:`,
          mappedProduct
        );
        return mappedProduct;
      } catch (innerError) {
        console.error(
          `❌ Error mapping product at index ${index}:`,
          innerError,
          product
        );
        return {
          id: "",
          facilityId: "",
          scId: "",
          productDetails: {
            productNo: "",
            standardLabelGrade: "",
            productCategory: "",
            productCategoryUserSpecificTerm: "",
            productDetail: "",
            productDetailUserSpecificTerm: "",
          },
        };
      }
    });

    console.log(`✅ Completed mapping all products.`);
    return mappedData;
  } catch (error) {
    console.error("❌ Error in mapProductData function:", error, rawProducts);
    return [];
  }
}

export const mapFacilityProductPayload = (
  input: any,
  scId: string,
  productId?: string
): any => {
  return {
    scId: scId,
    ...(productId && { productId }),
    facilityProductDetails: [
      {
        scProductNo: input.productDetails.productNo || "",
        scProductStandardLabelGrade:
          input.productDetails.standardLabelGrade || [],
        scProductStandardLabelGradeDescription:
          useTransactionCertificateStore
            .getState()
            .getFPDStdLblGrd.filter((option: any) =>
              input.productDetails.standardLabelGrade.includes(option.value)
            )
            .map((option: any) => option.label.trim()) || [],
        scProductCategoryCode: input.productDetails.productCategory || "",
        scProductCategoryCodeDescription:
          useTransactionCertificateStore
            .getState()
            .getFPDPrdtCat.find(
              (option: any) =>
                option.value === (input.productDetails.productCategory ?? "")
            )
            ?.label.trim() ?? "",
        scProductCategoryUserSpecificTerm:
          input.productDetails.productCategoryUserSpecificTerm || "",
        scProductDetailCode: input.productDetails.productDetail || "",
        scProductDetailCodeDescription:
          useTransactionCertificateStore
            .getState()
            .getFPDPrdtDtl.find(
              (option: any) =>
                option.value === (input.productDetails.productDetail ?? "")
            )
            ?.label.trim() ?? "",
        scProductDetailUserSpecificTerm:
          input.productDetails.productDetailUserSpecificTerm || "",
        associatedFacilityNo: input.productDetails.associatedFacilityNo || "",

        // Component Mapping
        scProductComponentDetails: input.componentDetails.map((comp: any) => {
          return {
            scProductComponentNo: comp?.productComponentNo ?? "",
            scComponentDetail: comp?.componentDetail ?? "",
            scComponentStandardLabelGrade:
              comp?.componentStandardLabelGrade ?? [],
            scComponentStandardLabelGradeDescription:
              useTransactionCertificateStore
                .getState()
                .getFPDStdLblGrd.filter(
                  (option: any) =>
                    comp.componentStandardLabelGrade &&
                    comp.componentStandardLabelGrade?.includes(option.value)
                )
                .map((option: any) => option.label.trim()) || [],
            // Component Raw Materials
            scProductComponentRawMaterialDetails:
              input.componentRawMaterialDetails
                .filter((rm: ComponentRawMaterialDetails) => rm?.productComponentNo === comp?.productComponentNo)
                .map(
                  (raw: {
                    componentRawMaterialCode: any;
                    componentRawMaterialPercentage: any;
                  }) => ({
                    scComponentRawMaterialCode:
                      raw.componentRawMaterialCode || "",
                    scComponentRawMaterialCodeDescription:
                      useTransactionCertificateStore
                        .getState()
                        .getFPDPrdtRawMtrlCodes.find(
                          (option: any) =>
                            option.value === (raw.componentRawMaterialCode ?? "")
                        )
                        ?.label.trim() ?? "",
                    scComponentRawMaterialPercentage: String(
                      raw.componentRawMaterialPercentage || ""
                    ),
                    additionalProp1: {},
                  })
                ),
            additionalProp1: {},
          };
        }),
        //  [
        //   {
        //     scProductComponentNo:
        //       input.componentDetails.productComponentNo || "",
        //     scComponentDetail: input.componentDetails.componentDetail || "",
        //     scComponentStandardLabelGrade:
        //       input.componentDetails.componentStandardLabelGrade || [],
        //     scComponentStandardLabelGradeDescription:
        //       useTransactionCertificateStore
        //         .getState()
        //         .getFPDStdLblGrd.filter(
        //           (option: any) =>
        //             input.componentDetails.componentStandardLabelGrade &&
        //             input.componentDetails.componentStandardLabelGrade?.includes(
        //               option.value
        //             )
        //         )
        //         .map((option: any) => option.label.trim()) || [],

        //     // Component Raw Materials
        //     scProductComponentRawMaterialDetails:
        //       input.componentRawMaterialDetails.map(
        //         (raw: {
        //           componentRawMaterialCode: any;
        //           componentRawMaterialPercentage: any;
        //         }) => ({
        //           scComponentRawMaterialCode:
        //             raw.componentRawMaterialCode || "",
        //           scComponentRawMaterialCodeDescription:
        //             useTransactionCertificateStore
        //               .getState()
        //               .getFPDPrdtRawMtrlCodes.find(
        //                 (option: any) =>
        //                   option.value === (raw.componentRawMaterialCode ?? "")
        //               )
        //               ?.label.trim() ?? "",
        //           scComponentRawMaterialPercentage: String(
        //             raw.componentRawMaterialPercentage || ""
        //           ),
        //           additionalProp1: {},
        //         })
        //       ),

        //     additionalProp1: {},
        //   },
        // ],

        // Product Raw Materials
        facilityProductRawMaterialDetails: input.rawMaterialDetails.map(
          (raw: { rawMaterialCode: any; rawMaterialPercentage: any }) => ({
            scProductRawMaterialCode: raw.rawMaterialCode || "",
            scProductRawMaterialCodeDescription:
              useTransactionCertificateStore
                .getState()
                .getFPDPrdtRawMtrlCodes.find(
                  (option: any) => option.value === (raw.rawMaterialCode ?? "")
                )
                ?.label.trim() ?? "",
            scProductRawMaterialPercentage: String(
              raw.rawMaterialPercentage || ""
            ),
            additionalProp1: {},
          })
        ),

        additionalProp1: {},
      },
    ],

    additionalProp1: {},
  };
};
